import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleOutline, CancelOutlined, Star, Bolt, Rocket,
  CreditCard, QrCode2, Analytics, People, FileDownload,
  Nfc, Visibility, WorkspacePremium,
} from '@mui/icons-material';
import { useRazorpay } from '../hooks/useRazorpay';
import { useAuth } from '../contexts/AuthContext';
import MembershipShippingDialog from '../components/MembershipShippingDialog';
import api from '../services/api';
import { fmtDate } from '../utils/dateFormat';

const PRIMARY = '#00A7E3';

/** Display name for current plan (API still uses `lifetime`) */
function planDisplayName(planKey) {
  if (!planKey || planKey === 'free') return 'Free';
  if (planKey === 'lifetime') return 'Lifetime Access';
  return planKey.charAt(0).toUpperCase() + planKey.slice(1);
}

const Subscription = () => {
  const { user } = useAuth();
  const { subscribe, payLifetime, loading: paymentLoading, shippingOpen, closeShippingDialog, onShippingSaved } = useRazorpay();
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState(null);
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/payments/subscription');
      if (data.success) setSubData(data);
    } catch { console.error('Failed to fetch subscription'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post('/payments/cancel');
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to cancel' });
    } finally {
      setCancelling(false);
      setShowCancel(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress size={32} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  const plan = subData?.plan || 'free';
  const limits = subData?.limits || { cards: 1, qr: 2, analytics: false, nfc: 0 };
  const usage = subData?.usage || { cards: 0, qr: 0 };
  const sub = subData?.subscription;
  const isLifetime = plan === 'lifetime';
  const isRecurringActive = sub && sub.status === 'active' && !sub.isLifetime;
  return (
    <Box sx={{ width: '100%', maxWidth: 1080, mx: 'auto', py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
          Your Digital Identity Platform
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 680, mx: 'auto', lineHeight: 1.7 }}>
          Create smart digital visiting cards, share QR codes, track engagement,
          and connect instantly — all in one platform.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Lifetime (one-time) */}
      {isLifetime && (
        <Alert severity="success" icon={<CheckCircleOutline />} sx={{ mb: 4, borderRadius: 2 }}>
          <strong>Lifetime Access Active</strong>
          <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
            One-time purchase — no renewal.
          </Typography>
        </Alert>
      )}

      {/* Active yearly subscription */}
      {isRecurringActive && (
        <Alert severity="success" icon={<CheckCircleOutline />} sx={{ mb: 4, borderRadius: 2 }}>
          <strong>{planDisplayName(plan)} Plan Active</strong>
          {sub.expiresAt && <> — Renews {fmtDate(sub.expiresAt)}</>}
          <Button size="small" color="error" variant="text"
            sx={{ ml: 2, textTransform: 'none', fontSize: '12px' }}
            onClick={() => setShowCancel(true)}>
            Cancel
          </Button>
        </Alert>
      )}

      {/* ═══════════════════════════════════════ */}
      {/*  3 PLAN CARDS                          */}
      {/* ═══════════════════════════════════════ */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 6 }}>

        {/* ── FREE ── */}
        <PlanTier
          icon={<Bolt sx={{ fontSize: 28, color: '#22c55e' }} />}
          name="Free"
          tagline="Perfect for individuals who want a digital identity and quick sharing."
          price="0"
          period="Forever"
          current={plan === 'free'}
          features={[
            { text: '1 Digital Visiting Card', icon: <CreditCard /> },
            { text: '2 QR Codes', icon: <QrCode2 /> },
            { text: 'Share via link, QR or NFC', icon: <Visibility /> },
            { text: 'Card editor & templates', icon: <Star /> },
          ]}
          limitations={[
            'No analytics',
            'Limited QR codes',
            'No NFC card included',
          ]}
          buttonLabel={plan === 'free' ? null : null}
        />

        {/* ── PRO ── */}
        <PlanTier
          icon={<Star sx={{ fontSize: 28, color: '#f59e0b' }} />}
          name="Pro"
          tagline="Best for professionals, freelancers, and salespeople who want to track engagement."
          price="499"
          period="/year"
          badge="RECOMMENDED"
          highlight
          current={plan === 'pro'}
          nfcBadge="1 FREE NFC Card Included"
          features={[
            { text: '1 Digital Card with full analytics', icon: <CreditCard /> },
            { text: '5 QR Codes with scan analytics', icon: <QrCode2 /> },
            { text: 'Real-time visitor tracking', icon: <Analytics /> },
            { text: 'Location, device & OS insights', icon: <Visibility /> },
            { text: 'Export analytics as CSV / PDF', icon: <FileDownload /> },
            { text: '1 Physical NFC Visiting Card', icon: <Nfc /> },
          ]}
          onSubscribe={() => subscribe('pro')}
          disabled={paymentLoading || plan === 'pro' || plan === 'business' || plan === 'lifetime'}
          buttonLabel={plan === 'pro' ? null : 'Get Pro — ₹499 + GST/yr'}
        />

        {/* ── BUSINESS ── */}
        <PlanTier
          icon={<Rocket sx={{ fontSize: 28, color: '#8b5cf6' }} />}
          name="Business"
          tagline="Ideal for companies, agencies, and teams managing multiple digital identities."
          price="999"
          period="/year"
          current={plan === 'business'}
          nfcBadge="5 FREE NFC Cards Included"
          features={[
            { text: 'Up to 5 Digital Cards with analytics', icon: <CreditCard /> },
            { text: 'Unlimited QR Codes with analytics', icon: <QrCode2 /> },
            { text: 'Advanced visitor & campaign insights', icon: <Analytics /> },
            { text: 'Team card management', icon: <People /> },
            { text: 'Export analytics reports', icon: <FileDownload /> },
            { text: 'Up to 5 Physical NFC Cards', icon: <Nfc /> },
          ]}
          onSubscribe={() => subscribe('business')}
          disabled={paymentLoading || plan === 'business'}
          buttonLabel={plan === 'business' ? null : 'Get Business — ₹999 + GST/yr'}
        />

        {/* ── LIFETIME (no highlight — only Pro is “recommended”; same visual weight as Business) ── */}
        <PlanTier
          icon={<WorkspacePremium sx={{ fontSize: 28, color: '#b45309' }} />}
          name="Lifetime Access"
          tagline="Best for professionals, freelancers, and salespeople who want to track engagement."
          price="2499"
          period="one-time"
          current={plan === 'lifetime'}
          accentBorder="#ca8a04"
          ctaAccent="#b45309"
          nfcBadge="1 FREE NFC Card Included"
          features={[
            { text: '1 Digital Card with full analytics', icon: <CreditCard /> },
            { text: '5 QR Codes with scan analytics', icon: <QrCode2 /> },
            { text: 'Real-time visitor tracking', icon: <Analytics /> },
            { text: 'Location, device & OS insights', icon: <Visibility /> },
            { text: 'Export analytics as CSV / PDF', icon: <FileDownload /> },
            { text: '1 Physical NFC Visiting Card', icon: <Nfc /> },
          ]}
          onSubscribe={() => payLifetime()}
          disabled={paymentLoading || plan === 'lifetime' || plan === 'business'}
          buttonLabel={plan === 'lifetime' ? null : 'Get Lifetime Access — ₹2499 + 18% GST'}
        />
      </Box>

      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 6 }}>
        All prices exclude 18% GST · Secure payment via Razorpay · Auto-renews yearly · Cancel anytime
      </Typography>

      {/* ═══════════════════════════════════════ */}
      {/*  COMPARISON TABLE                      */}
      {/* ═══════════════════════════════════════ */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', mb: 3 }}>
          Compare Plans
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            overflowX: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <Table sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.08) : t.palette.grey[50]),
                }}
              >
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '14px' }}>Feature</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '14px' }}>Free</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: PRIMARY, fontSize: '14px' }}>Pro</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#8b5cf6', fontSize: '14px' }}>Business</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#b45309', fontSize: '14px' }}>Lifetime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { label: 'Digital Cards', free: '1', pro: '1', biz: '5', life: '1' },
                { label: 'QR Codes', free: '2', pro: '5', biz: 'Unlimited', life: '5' },
                { label: 'Analytics', free: false, pro: true, biz: true, life: true },
                { label: 'NFC Card Included', free: false, pro: '1', biz: '5', life: '1' },
                { label: 'Visitor Insights', free: false, pro: true, biz: true, life: true },
                { label: 'Location Tracking', free: false, pro: true, biz: true, life: true },
                { label: 'Export Reports', free: false, pro: true, biz: true, life: true },
                { label: 'Team Management', free: false, pro: false, biz: true, life: false },
                { label: 'Priority Support', free: false, pro: false, biz: true, life: false },
              ].map((row, i) => (
                <TableRow
                  key={row.label}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === 'dark'
                        ? i % 2 === 0
                          ? alpha(t.palette.common.white, 0.04)
                          : alpha(t.palette.common.white, 0.02)
                        : i % 2 === 0
                          ? '#fff'
                          : '#fafafa',
                  }}
                >
                  <TableCell sx={{ fontWeight: 500, fontSize: '13px', color: 'text.primary' }}>{row.label}</TableCell>
                  <TableCell align="center">{renderCell(row.free)}</TableCell>
                  <TableCell align="center">{renderCell(row.pro)}</TableCell>
                  <TableCell align="center">{renderCell(row.biz)}</TableCell>
                  <TableCell align="center">{renderCell(row.life)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ═══════════════════════════════════════ */}
      {/*  YOUR CURRENT USAGE                    */}
      {/* ═══════════════════════════════════════ */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, p: 3, mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
          Your Current Usage
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          <UsageStat label="Plan" value={planDisplayName(plan)} />
          <UsageStat label="Digital Cards" value={`${usage.cards} / ${limits.cards ?? '∞'}`} />
          <UsageStat label="QR Codes" value={`${usage.qr} / ${limits.qr ?? '∞'}`} />
          <UsageStat label="Analytics" value={limits.analytics ? 'Enabled' : 'Disabled'} color={limits.analytics ? '#16a34a' : '#ef4444'} />
        </Box>
      </Paper>

      {/* ───── Cancel Dialog ───── */}
      <MembershipShippingDialog
        open={shippingOpen}
        onClose={closeShippingDialog}
        user={user}
        onSaved={onShippingSaved}
      />

      <Dialog open={showCancel} onClose={() => setShowCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            Your subscription will be cancelled at the end of the current billing period.
            You'll retain access until then. After that, your account will revert to the Free plan.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowCancel(false)} sx={{ textTransform: 'none' }}>Keep subscription</Button>
          <Button onClick={handleCancel} disabled={cancelling} color="error" variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, boxShadow: 'none' }}>
            {cancelling ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═══════════════════════════════════════
//  Plan Tier Card
// ═══════════════════════════════════════
function PlanTier({ icon, name, tagline, price, period, badge, highlight, current, accentBorder, ctaAccent, nfcBadge, features, limitations, onSubscribe, disabled, buttonLabel }) {
  return (
    <Card sx={{
      borderRadius: 2.5,
      bgcolor: 'background.paper',
      border: highlight ? '2px solid' : '1px solid',
      borderColor: current ? '#16a34a' : highlight ? PRIMARY : accentBorder || 'divider',
      overflow: 'visible', position: 'relative',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      display: 'flex', flexDirection: 'column',
      ...(highlight && !current ? {
        boxShadow: `0 4px 24px ${PRIMARY}22`,
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${PRIMARY}30` },
      } : {
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (t) => (t.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.08)'),
        },
      }),
    }}>
      {badge && (
        <Chip label={badge} size="small" sx={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          bgcolor: PRIMARY, color: '#fff', fontWeight: 800, fontSize: '10px', height: 24,
        }} />
      )}
      {current && (
        <Chip label="CURRENT PLAN" size="small" sx={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          bgcolor: '#16a34a', color: '#fff', fontWeight: 800, fontSize: '10px', height: 24,
        }} />
      )}
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {icon}
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>{name}</Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '13px', lineHeight: 1.6, minHeight: { md: 48 } }}>
          {tagline}
        </Typography>

        <Box sx={{ mb: 0.5 }}>
          {price === '0' ? (
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>Free</Typography>
          ) : (
            <>
              <Typography component="span" variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>₹{price}</Typography>
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 0.5 }}>{period}</Typography>
            </>
          )}
        </Box>
        {price !== '0' && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>+ 18% GST</Typography>
        )}
        {price === '0' && <Box sx={{ mb: 2 }} />}

        {nfcBadge && (
          <Chip
            icon={<Nfc sx={{ fontSize: 14, color: '#fbbf24 !important' }} />}
            label={nfcBadge}
            size="small"
            sx={{
              bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a',
              fontWeight: 700, fontSize: '11px', height: 28, mb: 2, alignSelf: 'flex-start',
            }}
          />
        )}

        <Box sx={{ flex: 1, mb: 2.5 }}>
          {features.map(f => (
            <Box key={f.text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
              <CheckCircleOutline sx={{ fontSize: 15, color: '#16a34a', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.primary', opacity: 0.92 }}>{f.text}</Typography>
            </Box>
          ))}
          {limitations?.map(l => (
            <Box key={l} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
              <CancelOutlined sx={{ fontSize: 15, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>{l}</Typography>
            </Box>
          ))}
        </Box>

        {buttonLabel ? (
          <Button
            fullWidth variant={highlight ? 'contained' : 'outlined'}
            onClick={onSubscribe} disabled={disabled}
            sx={{
              borderRadius: 1.5, textTransform: 'none', fontWeight: 700, py: 1.25, boxShadow: 'none',
              ...(highlight
                ? {
                    bgcolor: PRIMARY,
                    color: '#fff',
                    '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
                  }
                : ctaAccent
                  ? {
                      borderColor: ctaAccent,
                      color: ctaAccent,
                      borderWidth: 2,
                      '&:hover': { borderColor: ctaAccent, bgcolor: (t) => alpha(ctaAccent, t.palette.mode === 'dark' ? 0.2 : 0.08) },
                    }
                  : {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      borderWidth: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.18 : 0.08),
                      },
                    }
              ),
            }}>
            {buttonLabel}
          </Button>
        ) : current ? (
          <Chip label="CURRENT PLAN" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, width: '100%', height: 40 }} />
        ) : (
          <Box sx={{ height: 40 }} />
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════
//  Comparison cell renderer
// ═══════════════════════════════════════
function renderCell(val) {
  if (val === true) return <CheckCircleOutline sx={{ fontSize: 18, color: '#16a34a' }} />;
  if (val === false) return <CancelOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />;
  return <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: 'text.primary' }}>{val}</Typography>;
}

// ═══════════════════════════════════════
//  Usage stat chip
// ═══════════════════════════════════════
function UsageStat({ label, value, color }) {
  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default Subscription;
